import React, { useState } from 'react';
import { Card, Row, Col, Select, DatePicker, Button, Table, Spin, message } from 'antd';
import { FileExcelOutlined, BarChartOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { TeachingHoursByTeacher, TeachingHoursStatisticsQuery } from '../../../../shared/types/database';
import { statisticService } from '../../services/statisticService';

// Các chế độ lọc
const filterModes = [
  { label: 'Theo ngày', value: 'date' },
  { label: 'Theo tháng', value: 'month' },
  { label: 'Theo quý', value: 'quarter' },
  { label: 'Theo năm', value: 'year' }
];

// Kiểm tra xem có phải ngày chủ nhật không
const isSunday = (date: Date | string): boolean => {
  if (!date) return false;
  const d = dayjs(date);
  return d.day() === 0;
};

const TeachingHoursByTeacherPage: React.FC = () => {
  const [mode, setMode] = useState<'date' | 'month' | 'quarter' | 'year'>('date');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [quarter, setQuarter] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<number>(dayjs().year());
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<TeachingHoursByTeacher[]>([]);

  // Định nghĩa các cột của bảng
  const columns: ColumnsType<TeachingHoursByTeacher> = [
    {
      title: 'STT',
      render: (_, __, index) => index + 1,
      width: 50,
      align: 'center'
    },
    {
      title: 'Ngày giảng',
      dataIndex: 'NgayGiang',
      key: 'NgayGiang',
      render: (value: Date, record: TeachingHoursByTeacher) => {
        const isToi = record.BuoiGiang && record.BuoiGiang.trim().toLowerCase() === 'tối';
        const isSun = isSunday(value);
        const highlight = isToi || isSun;
        
        return (
          <span style={highlight ? { 
            background: '#e6f7ff', 
            color: '#096dd9', 
            fontWeight: 600, 
            borderRadius: 4, 
            padding: '2px 8px' 
          } : {}}>
            {value ? dayjs(value).format('DD/MM/YYYY') : ''}
          </span>
        );
      },
      sorter: (a, b) => dayjs(a.NgayGiang).unix() - dayjs(b.NgayGiang).unix(),
      filters: Array.from(new Set(data.map(row => dayjs(row.NgayGiang).format('DD/MM/YYYY'))))
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => dayjs(record.NgayGiang).format('DD/MM/YYYY') === value,
      width: 120
    },
    {
      title: 'Buổi giảng',
      dataIndex: 'BuoiGiang',
      key: 'BuoiGiang',
      sorter: (a, b) => (a.BuoiGiang || '').localeCompare(b.BuoiGiang || ''),
      filters: Array.from(new Set(data.map(row => row.BuoiGiang)))
        .filter(v => !!v)
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => record.BuoiGiang === value,
      width: 100
    },
    {
      title: 'Buổi số',
      dataIndex: 'BuoiSo',
      key: 'BuoiSo',
      align: 'center',
      sorter: (a, b) => (a.BuoiSo || 0) - (b.BuoiSo || 0),
      width: 90
    },
    {
      title: 'Tên bài giảng',
      dataIndex: 'TenBaiGiang',
      key: 'TenBaiGiang',
      sorter: (a, b) => (a.TenBaiGiang || '').localeCompare(b.TenBaiGiang || ''),
      filters: Array.from(new Set(data.map(row => row.TenBaiGiang)))
        .filter(v => !!v)
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => record.TenBaiGiang === value,
      width: 200
    },
    {
      title: 'Khóa học',
      dataIndex: 'TenKhoaHoc',
      key: 'TenKhoaHoc',
      sorter: (a, b) => (a.TenKhoaHoc || '').localeCompare(b.TenKhoaHoc || ''),
      filters: Array.from(new Set(data.map(row => row.TenKhoaHoc)))
        .filter(v => !!v)
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => record.TenKhoaHoc === value,
      width: 150
    },
    {
      title: 'Môn học',
      dataIndex: 'TenMonHoc',
      key: 'TenMonHoc',
      sorter: (a, b) => (a.TenMonHoc || '').localeCompare(b.TenMonHoc || ''),
      filters: Array.from(new Set(data.map(row => row.TenMonHoc)))
        .filter(v => !!v)
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => record.TenMonHoc === value,
      width: 180
    },
    {
      title: 'Lớp học phần',
      dataIndex: 'TenLopHP',
      key: 'TenLopHP',
      sorter: (a, b) => (a.TenLopHP || '').localeCompare(b.TenLopHP || ''),
      filters: Array.from(new Set(data.map(row => row.TenLopHP)))
        .filter(v => !!v)
        .map(val => ({ text: val, value: val })),
      onFilter: (value, record) => record.TenLopHP === value,
      width: 150
    },
    {
      title: 'Số tiết LT',
      dataIndex: 'SoTietLT',
      key: 'SoTietLT',
      sorter: (a, b) => (a.SoTietLT || 0) - (b.SoTietLT || 0),
      align: 'center',
      width: 100
    },
    {
      title: 'Số tiết TH',
      dataIndex: 'SoTietTH',
      key: 'SoTietTH',
      sorter: (a, b) => (a.SoTietTH || 0) - (b.SoTietTH || 0),
      align: 'center',
      width: 100
    },
    {
      title: 'Sĩ số',
      dataIndex: 'SiSo',
      key: 'SiSo',
      sorter: (a, b) => (a.SiSo || 0) - (b.SiSo || 0),
      align: 'center',
      width: 80
    }
  ];

  // Tổng hợp cuối bảng
  const tableSummary = (pageData: readonly TeachingHoursByTeacher[]) => {
    const filteredLT = pageData.reduce((sum, row) => sum + (row.SoTietLT || 0), 0);
    const filteredTH = pageData.reduce((sum, row) => sum + (row.SoTietTH || 0), 0);
    
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={8}>
          <strong>TỔNG</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={8} align="center">
          <strong>{filteredLT}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={9} align="center">
          <strong>{filteredTH}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={10}></Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  // Xử lý thống kê
  const handleStatistic = async () => {
    setLoading(true);
    setData([]);
    
    try {
      // Kiểm tra token trước khi gọi API
      const token = localStorage.getItem('quanly_truonghoc_token') || localStorage.getItem('token');
      if (!token) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        return;
      }
      
      const query: TeachingHoursStatisticsQuery = { mode };
      
      if (mode === 'date') {
        if (!fromDate || !toDate) {
          message.warning('Chọn đủ từ ngày và đến ngày!');
          return;
        }
        query.fromDate = fromDate.format('YYYY-MM-DD');
        query.toDate = toDate.format('YYYY-MM-DD');
      } else if (mode === 'month') {
        if (!month || !year) {
          message.warning('Chọn đủ tháng và năm!');
          return;
        }
        query.month = month;
        query.year = year;
      } else if (mode === 'quarter') {
        if (!quarter || !year) {
          message.warning('Chọn đủ quý và năm!');
          return;
        }
        query.quarter = quarter;
        query.year = year;
      } else if (mode === 'year') {
        if (!year) {
          message.warning('Chọn năm!');
          return;
        }
        query.year = year;
      }
      
      // Lấy mã giáo viên từ localStorage hoặc token
      const userInfo = JSON.parse(localStorage.getItem('quanly_truonghoc_user') || '{}');
      let teacherId = userInfo.Username || userInfo.username;
      
      // Fallback: thử key cũ
      if (!teacherId) {
        const oldUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        teacherId = oldUserInfo.Username || oldUserInfo.username;
      }
      
      if (!teacherId) {
        message.error('Không tìm thấy thông tin giáo viên. Vui lòng đăng nhập lại.');
        return;
      }
      
      query.teacherId = teacherId;
      console.log('Query for teaching hours:', query);
      console.log('User info:', userInfo);
      console.log('Teacher ID:', teacherId);
      
      const result = await statisticService.getTeachingHoursByTeacher(query);
      console.log('API result:', result);
      setData(result);
      
      if (result.length === 0) {
        message.info('Không có dữ liệu giờ giảng trong khoảng thời gian đã chọn');
      }
      
    } catch (error) {
      console.error('Error fetching teaching hours:', error);
      const errorMessage = (error as Error).message;
      
      if (errorMessage.includes('Token') || errorMessage.includes('JWT') || errorMessage.includes('401')) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        // Có thể redirect về trang login
        // window.location.href = '/login';
      } else {
        message.error('Lỗi khi lấy thống kê: ' + errorMessage);
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Xuất Excel
  const handleExportExcel = async () => {
    if (!data.length) {
      message.warning('Không có dữ liệu để xuất!');
      return;
    }
    
    try {
      const exportData = data.map((row, idx) => ({
        'STT': idx + 1,
        'Ngày giảng': row.NgayGiang ? dayjs(row.NgayGiang).format('DD/MM/YYYY') : '',
        'Buổi giảng': row.BuoiGiang || '',
        'Buổi số': row.BuoiSo ?? '',
        'Tên bài giảng': row.TenBaiGiang || '',
        'Khóa học': row.TenKhoaHoc || '',
        'Môn học': row.TenMonHoc || '',
        'Lớp học phần': row.TenLopHP || '',
        'Số tiết LT': row.SoTietLT || 0,
        'Số tiết TH': row.SoTietTH || 0,
        'Sĩ số': row.SiSo || 0
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Thống kê giờ giảng');
      
      // Generate filename with timestamp
      const timestamp = dayjs().format('YYYYMMDD_HHmmss');
      const filename = `ThongKeGioGiang_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      message.success('Xuất Excel thành công!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Lỗi khi xuất Excel: ' + (error as Error).message);
    }
  };

  return (
    <Card title="Thống kê giờ giảng của giáo viên">
      <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Select
            style={{ width: '100%' }}
            value={mode}
            onChange={setMode}
            placeholder="Chọn chế độ"
            options={filterModes}
          />
        </Col>
        
        {mode === 'date' && (
          <>
            <Col span={4}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Từ ngày"
                value={fromDate}
                onChange={setFromDate}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col span={4}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Đến ngày"
                value={toDate}
                onChange={setToDate}
                format="DD/MM/YYYY"
              />
            </Col>
          </>
        )}
        
        {mode === 'month' && (
          <>
            <Col span={4}>
              <Select
                style={{ width: '100%' }}
                placeholder="Tháng"
                value={month}
                onChange={setMonth}
                options={Array.from({ length: 12 }, (_, i) => ({
                  label: `Tháng ${i + 1}`,
                  value: i + 1
                }))}
              />
            </Col>
            <Col span={4}>
              <Select
                style={{ width: '100%' }}
                placeholder="Năm"
                value={year}
                onChange={setYear}
                options={Array.from({ length: 6 }, (_, i) => {
                  const yearValue = dayjs().year() - i;
                  return {
                    label: yearValue.toString(),
                    value: yearValue
                  };
                })}
              />
            </Col>
          </>
        )}
        
        {mode === 'quarter' && (
          <>
            <Col span={4}>
              <Select
                style={{ width: '100%' }}
                placeholder="Quý"
                value={quarter}
                onChange={setQuarter}
                options={[1, 2, 3, 4].map(q => ({
                  label: `Quý ${q}`,
                  value: q
                }))}
              />
            </Col>
            <Col span={4}>
              <Select
                style={{ width: '100%' }}
                placeholder="Năm"
                value={year}
                onChange={setYear}
                options={Array.from({ length: 6 }, (_, i) => {
                  const yearValue = dayjs().year() - i;
                  return {
                    label: yearValue.toString(),
                    value: yearValue
                  };
                })}
              />
            </Col>
          </>
        )}
        
        {mode === 'year' && (
          <Col span={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Năm"
              value={year}
              onChange={setYear}
              options={Array.from({ length: 6 }, (_, i) => {
                const yearValue = dayjs().year() - i;
                return {
                  label: yearValue.toString(),
                  value: yearValue
                };
              })}
            />
          </Col>
        )}
        
        <Col flex="none">
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={handleStatistic}
            loading={loading}
            style={{ minWidth: 140, marginRight: 8 }}
          >
            Thống kê giờ giảng
          </Button>
        </Col>
        
        <Col flex="none">
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={!data.length}
            style={{ minWidth: 120 }}
          >
            Xuất Excel
          </Button>
        </Col>
      </Row>
      
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(_, index) => index || 0}
          bordered
          scroll={{ x: true }}
          pagination={false}
          summary={tableSummary}
        />
      </Spin>
    </Card>
  );
};

export default TeachingHoursByTeacherPage;
